import type { GetAllOffersResponse, ProgramDetailsOfferItem } from "../shared/publicOffer.types";
import {
  mapProgramOfferToPublicOffer,
  resolvePublicAssetUrl,
} from "../shared/publicOffer.utils";
import type { StorefrontCatalog, StorefrontOfferEntry } from "./types";

const LIMITED_TIME_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

type OfferMappingContext = {
  apiBaseUrl: string;
  programName: string;
  programLogoUrl?: string;
  defaultMerchantImageUrl?: string;
};

function buildOfferMappingContext(
  data: GetAllOffersResponse,
  apiBaseUrl: string
): OfferMappingContext {
  const programList = Array.isArray(data.programVO)
    ? data.programVO
    : data.programVO
      ? [data.programVO]
      : [];
  const firstProgram = programList.find((p) => p?.xssAttack !== true) ?? programList[0];
  const merchants = data.merchantVOList || [];

  return {
    apiBaseUrl,
    programName: (firstProgram?.programName || "").trim() || "Storefront",
    programLogoUrl: resolvePublicAssetUrl(apiBaseUrl, firstProgram?.programLogoUri),
    defaultMerchantImageUrl: resolvePublicAssetUrl(
      apiBaseUrl,
      merchants.find((m) => (m.merchantImageUri || "").trim())?.merchantImageUri
    ),
  };
}

function toStorefrontEntry(
  raw: ProgramDetailsOfferItem,
  ctx: OfferMappingContext
): StorefrontOfferEntry {
  const merchantName =
    (raw.merchantOfOfferVO?.merchantName || "").trim() || ctx.programName;
  const offer = mapProgramOfferToPublicOffer(raw, {
    ...ctx,
    programName: merchantName,
  });
  const startDateInMillis =
    raw.startDateInMillis != null && Number.isFinite(Number(raw.startDateInMillis))
      ? Number(raw.startDateInMillis)
      : 0;

  return {
    offer,
    merchantName,
    categoryName: (raw.categoryVO?.categoryName || "").trim() || undefined,
    cashbackPercentage:
      raw.categoryVO?.cashbackPercentage != null &&
      Number.isFinite(Number(raw.categoryVO.cashbackPercentage))
        ? Number(raw.categoryVO.cashbackPercentage)
        : undefined,
    isActive: raw.isActive === true,
    startDateInMillis,
  };
}

function mapOfferItems(
  items: ProgramDetailsOfferItem[] | undefined,
  ctx: OfferMappingContext
): StorefrontOfferEntry[] {
  return (items || [])
    .filter((raw) => raw && raw.xssAttack !== true)
    .map((raw) => toStorefrontEntry(raw, ctx));
}

function filterLimitedTimeOffers(
  items: ProgramDetailsOfferItem[] | undefined,
  ctx: OfferMappingContext
): StorefrontOfferEntry[] {
  const now = Date.now();
  return mapOfferItems(items, ctx).filter((entry) => {
    const endMs = entry.offer.expiresAtMillis;
    return (
      endMs != null && endMs > now && endMs - now <= LIMITED_TIME_WINDOW_MS
    );
  });
}

export function mapGetAllOffersResponse(
  data: unknown,
  apiBaseUrl: string
): StorefrontCatalog {
  const record = (data && typeof data === "object" ? data : {}) as GetAllOffersResponse;
  const ctx = buildOfferMappingContext(record, apiBaseUrl);

  return {
    discoveryOffers: mapOfferItems(record.merchantOffersVOList, ctx),
    bestDealOffers: mapOfferItems(record.bestDealsVOList, ctx),
    limitedTimeOffers: filterLimitedTimeOffers(record.merchantOffersVOList, ctx),
  };
}
